import React, { useState, useEffect } from "react";
import { AppConfig, DomainItem, DashboardStats, Bid, Backorder } from "../types";
import { DropCatchService } from "../services/dropcatchService";
import { GeminiService } from "../services/geminiService";

interface DashboardProps {
  config: AppConfig;
  dropcatchService: DropCatchService;
  geminiService: GeminiService;
  userName?: string;
  isEmailVerified?: boolean;
  userCreatedAt?: Date | string;
  hasDropCatchCredentials?: boolean;
  savedKeywords?: string[];
  onResendVerification?: () => Promise<void>;
  onSaveKeywords?: (keywords: string[]) => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  config,
  dropcatchService,
  userName,
  isEmailVerified = true,
  userCreatedAt,
  hasDropCatchCredentials = true,
  savedKeywords = [],
  onResendVerification,
  onSaveKeywords,
}) => {
  const [keywordsInput, setKeywordsInput] = useState("");
  const [keywordsSaved, setKeywordsSaved] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalAuctions: 0,
    activeBackorders: 0,
    activeBids: 0,
    winningBids: 0,
    totalSpent: 0,
    domainsWon: 0,
  });
  const [recentAuctions, setRecentAuctions] = useState<DomainItem[]>([]);
  const [myBids, setMyBids] = useState<Bid[]>([]);
  const [myBackorders, setMyBackorders] = useState<Backorder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDashboardData = async () => {
    if (!dropcatchService.isConfigured()) {
      setError("ERROR: DROPCATCH API NOT CONFIGURED. RUN CONFIG.EXE");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [auctionsResult, bidsResult, backordersResult] = await Promise.allSettled([
        dropcatchService.searchAuctions({ size: 10, showAllActive: true }),
        dropcatchService.getBids({ size: 10 }),
        dropcatchService.getBackorders({ size: 10 }),
      ]);

      if (auctionsResult.status === "fulfilled") {
        setRecentAuctions(auctionsResult.value.items);
        setStats((prev) => ({ ...prev, totalAuctions: auctionsResult.value.items.length }));
      }

      if (bidsResult.status === "fulfilled") {
        const bids = bidsResult.value.items;
        setMyBids(bids);
        const winning = bids.filter((b) => b.isWinning).length;
        setStats((prev) => ({
          ...prev,
          activeBids: bids.length,
          winningBids: winning,
        }));
      }

      if (backordersResult.status === "fulfilled") {
        setMyBackorders(backordersResult.value.items);
        setStats((prev) => ({
          ...prev,
          activeBackorders: backordersResult.value.items.length,
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "FATAL ERROR: DATA LOAD FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dropcatchService.isConfigured()) {
      loadDashboardData();
    }
  }, [config.dropcatch_client_id]);

  const formatTimeRemaining = (endTime: string) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end.getTime() - now.getTime();

    if (diff < 0) return "ENDED";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}D ${hours % 24}H`;
    }
    return `${hours}H ${minutes}M`;
  };

  return (
    <div className="p-6 space-y-6 bg-black min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          {userName && (
            <p className="text-green-500 text-sm mb-1">
              HELLO, <span className="text-green-400 font-bold">{userName.toUpperCase()}</span>
            </p>
          )}
          <h1 className="text-2xl font-bold text-green-400 tracking-wider">
            {">"} SYSTEM DASHBOARD
          </h1>
          <p className="text-green-600 text-sm mt-1">
            ZERO POINT DOMAINS COMMAND CENTER V.1.0.0
          </p>
        </div>
        <button
          onClick={loadDashboardData}
          disabled={loading || !dropcatchService.isConfigured()}
          className="flex items-center gap-2 px-4 py-2 bg-black border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed tracking-wider text-sm"
          style={{ boxShadow: "0 0 10px rgba(34, 197, 94, 0.3)" }}
        >
          {loading ? (
            <span className="animate-pulse">[LOADING...]</span>
          ) : (
            <span>[REFRESH DATA]</span>
          )}
        </button>
      </div>

      {/* Onboarding Checklist */}
      {(() => {
        const hasKeywords = savedKeywords.length > 0 || keywordsSaved;
        const allComplete = isEmailVerified && hasDropCatchCredentials && hasKeywords;
        
        // Calculate email verification deadline
        const createdDate = userCreatedAt ? new Date(userCreatedAt) : new Date();
        const deadline = new Date(createdDate.getTime() + 48 * 60 * 60 * 1000);
        const now = new Date();
        const remainingMs = deadline.getTime() - now.getTime();
        const hoursLeft = Math.max(0, Math.floor(remainingMs / (1000 * 60 * 60)));
        const minutesLeft = Math.max(0, Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60)));
        const isExpired = remainingMs <= 0;
        
        if (allComplete) return null;
        
        return (
          <div className="border-2 border-green-500 bg-black p-4" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-green-400 font-bold tracking-wider">*** SETUP CHECKLIST ***</h2>
              <span className="text-green-600 text-xs">
                {[isEmailVerified, hasDropCatchCredentials, hasKeywords].filter(Boolean).length}/3 COMPLETE
              </span>
            </div>
            
            <div className="space-y-3">
              {/* Email Verification */}
              <div className={`flex items-center justify-between p-3 border ${isEmailVerified ? 'border-green-700 bg-green-950/20' : isExpired ? 'border-red-500' : 'border-yellow-500'}`}>
                <div className="flex items-center gap-3">
                  <span className={`text-lg ${isEmailVerified ? 'text-green-500' : isExpired ? 'text-red-500' : 'text-yellow-500'}`}>
                    {isEmailVerified ? '[✓]' : '[ ]'}
                  </span>
                  <div>
                    <p className={`font-bold ${isEmailVerified ? 'text-green-500' : isExpired ? 'text-red-400' : 'text-yellow-400'}`}>
                      VERIFY EMAIL
                    </p>
                    <p className={`text-xs ${isEmailVerified ? 'text-green-700' : isExpired ? 'text-red-500' : 'text-yellow-500'}`}>
                      {isEmailVerified 
                        ? 'EMAIL VERIFIED' 
                        : isExpired 
                          ? 'OVERDUE - VERIFY NOW TO RESTORE ACCESS'
                          : `${hoursLeft}H ${minutesLeft}M REMAINING`
                      }
                    </p>
                  </div>
                </div>
                {!isEmailVerified && onResendVerification && (
                  <button
                    onClick={onResendVerification}
                    className={`px-3 py-1 border text-xs tracking-wider ${isExpired ? 'border-red-500 text-red-500 hover:bg-red-500' : 'border-yellow-500 text-yellow-500 hover:bg-yellow-500'} hover:text-black transition-colors`}
                  >
                    [RESEND]
                  </button>
                )}
              </div>
              
              {/* DropCatch Credentials */}
              <div className={`flex items-center justify-between p-3 border ${hasDropCatchCredentials ? 'border-green-700 bg-green-950/20' : 'border-cyan-500'}`}>
                <div className="flex items-center gap-3">
                  <span className={`text-lg ${hasDropCatchCredentials ? 'text-green-500' : 'text-cyan-500'}`}>
                    {hasDropCatchCredentials ? '[✓]' : '[ ]'}
                  </span>
                  <div>
                    <p className={`font-bold ${hasDropCatchCredentials ? 'text-green-500' : 'text-cyan-400'}`}>
                      CONNECT DROPCATCH API
                    </p>
                    <p className={`text-xs ${hasDropCatchCredentials ? 'text-green-700' : 'text-cyan-500'}`}>
                      {hasDropCatchCredentials ? 'API CONNECTED' : 'REQUIRED FOR AUCTION ACCESS'}
                    </p>
                  </div>
                </div>
                {!hasDropCatchCredentials && (
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('openSettings'))}
                    className="px-3 py-1 border border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-black transition-colors text-xs tracking-wider"
                  >
                    [CONFIGURE]
                  </button>
                )}
              </div>
              
              {/* Keywords Setup */}
              <div className={`p-3 border ${hasKeywords ? 'border-green-700 bg-green-950/20' : 'border-purple-500'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`text-lg ${hasKeywords ? 'text-green-500' : 'text-purple-500'}`}>
                      {hasKeywords ? '[✓]' : '[ ]'}
                    </span>
                    <div>
                      <p className={`font-bold ${hasKeywords ? 'text-green-500' : 'text-purple-400'}`}>
                        SET SEARCH KEYWORDS
                      </p>
                      <p className={`text-xs ${hasKeywords ? 'text-green-700' : 'text-purple-500'}`}>
                        {hasKeywords ? `${savedKeywords.length || keywordsInput.split(',').filter(k => k.trim()).length} KEYWORDS SAVED` : 'OPTIONAL - CUSTOMIZE YOUR DOMAIN SEARCHES'}
                      </p>
                    </div>
                  </div>
                </div>
                {!hasKeywords && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={keywordsInput}
                      onChange={(e) => setKeywordsInput(e.target.value)}
                      placeholder="law, legal, attorney, court..."
                      className="flex-1 px-3 py-2 bg-black border border-purple-700 text-purple-400 text-sm focus:border-purple-500 outline-none"
                    />
                    <button
                      onClick={() => {
                        const keywords = keywordsInput.split(',').map(k => k.trim()).filter(Boolean);
                        if (onSaveKeywords) onSaveKeywords(keywords);
                        setKeywordsSaved(true);
                      }}
                      className="px-3 py-1 border border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-black transition-colors text-xs tracking-wider"
                    >
                      [SAVE]
                    </button>
                    <button
                      onClick={() => setKeywordsSaved(true)}
                      className="px-3 py-1 border border-purple-700 text-purple-600 hover:border-purple-500 hover:text-purple-400 transition-colors text-xs tracking-wider"
                    >
                      [SKIP]
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Error Banner */}
      {error && (
        <div className="border-2 border-green-500 bg-black p-4 animate-pulse">
          <p className="text-green-400 font-bold">*** ERROR ***</p>
          <p className="text-green-500 text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "ACTIVE AUCTIONS", value: stats.totalAuctions, id: "AUC" },
          { label: "WINNING BIDS", value: stats.winningBids, id: "WIN" },
          { label: "BACKORDERS", value: stats.activeBackorders, id: "BO" },
          { label: "ACTIVE BIDS", value: stats.activeBids, id: "BID" },
        ].map((stat) => (
          <div
            key={stat.id}
            className="border-2 border-green-500 bg-black p-4"
            style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}
          >
            <div className="text-green-600 text-xs mb-1">[{stat.id}]</div>
            <div className="text-3xl font-bold text-green-400 tabular-nums">
              {String(stat.value).padStart(3, "0")}
            </div>
            <div className="text-green-600 text-xs mt-1 tracking-wider">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Auctions */}
        <div className="border-2 border-green-500 bg-black" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
          <div className="px-4 py-3 border-b-2 border-green-500 flex justify-between items-center">
            <h2 className="font-bold text-green-400 tracking-wider">RECENT AUCTIONS</h2>
            <span className="text-xs text-green-600">{recentAuctions.length} RECORDS</span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {recentAuctions.length === 0 ? (
              <div className="px-4 py-8 text-center text-green-600">
                <pre className="text-green-500 text-xs mb-2">
{`  _______
 |       |
 | EMPTY |
 |_______|`}
                </pre>
                <p className="text-sm">NO AUCTION DATA</p>
                <p className="text-xs mt-1 text-green-700">EXECUTE REFRESH COMMAND</p>
              </div>
            ) : (
              recentAuctions.slice(0, 5).map((auction, i) => (
                <div
                  key={auction.id}
                  className="px-4 py-2 border-b border-green-900 hover:bg-green-950/30 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-green-700 text-xs mr-2">{String(i + 1).padStart(2, "0")}.</span>
                      <span className="font-semibold text-green-400">{auction.name}</span>
                      <p className="text-sm text-green-600 ml-4">{auction.bidders} BIDDERS</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-400">${auction.price}</p>
                      <p className="text-xs text-green-600">{formatTimeRemaining(auction.endTime)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* My Active Bids */}
        <div className="border-2 border-green-500 bg-black" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
          <div className="px-4 py-3 border-b-2 border-green-500 flex justify-between items-center">
            <h2 className="font-bold text-green-400 tracking-wider">MY ACTIVE BIDS</h2>
            <span className="text-xs text-green-600">{myBids.length} BIDS</span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {myBids.length === 0 ? (
              <div className="px-4 py-8 text-center text-green-600">
                <pre className="text-green-500 text-xs mb-2">
{`  _______
 |       |
 | EMPTY |
 |_______|`}
                </pre>
                <p className="text-sm">NO ACTIVE BIDS</p>
                <p className="text-xs mt-1 text-green-700">START BIDDING ON AUCTIONS</p>
              </div>
            ) : (
              myBids.slice(0, 5).map((bid, i) => (
                <div
                  key={bid.auctionId}
                  className="px-4 py-2 border-b border-green-900 hover:bg-green-950/30 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-green-700 text-xs mr-2">{String(i + 1).padStart(2, "0")}.</span>
                      <span className="font-semibold text-green-400">{bid.domain}</span>
                      <span
                        className={`ml-2 text-xs px-2 py-0.5 ${
                          bid.isWinning
                            ? "bg-green-500 text-black"
                            : "border border-green-500 text-green-500"
                        }`}
                      >
                        {bid.isWinning ? "WINNING" : "OUTBID"}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-400">${bid.highBid}</p>
                      <p className="text-xs text-green-600">MAX: ${bid.maxBid}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* My Backorders */}
        <div className="border-2 border-green-500 bg-black" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
          <div className="px-4 py-3 border-b-2 border-green-500 flex justify-between items-center">
            <h2 className="font-bold text-green-400 tracking-wider">MY BACKORDERS</h2>
            <span className="text-xs text-green-600">{myBackorders.length} PENDING</span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {myBackorders.length === 0 ? (
              <div className="px-4 py-8 text-center text-green-600">
                <pre className="text-green-500 text-xs mb-2">
{`  _______
 |       |
 | EMPTY |
 |_______|`}
                </pre>
                <p className="text-sm">NO ACTIVE BACKORDERS</p>
                <p className="text-xs mt-1 text-green-700">SETUP BACKORDERS FOR DROPPING DOMAINS</p>
              </div>
            ) : (
              myBackorders.slice(0, 5).map((bo, i) => (
                <div
                  key={bo.domain}
                  className="px-4 py-2 border-b border-green-900 hover:bg-green-950/30 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-green-700 text-xs mr-2">{String(i + 1).padStart(2, "0")}.</span>
                      <span className="font-semibold text-green-400">{bo.domain}</span>
                      <span
                        className={`ml-2 text-xs px-2 py-0.5 border ${
                          bo.type === "DiscountClub"
                            ? "border-green-400 text-green-400"
                            : "border-green-600 text-green-600"
                        }`}
                      >
                        {bo.type.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-600">
                        DROP: {new Date(bo.dropDate).toLocaleDateString()}
                      </p>
                      {bo.maxBid && (
                        <p className="text-xs text-green-700">MAX: ${bo.maxBid}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div
          className="border-2 border-green-500 bg-black p-4"
          style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}
        >
          <h2 className="font-bold text-green-400 tracking-wider mb-4">QUICK COMMANDS</h2>
          <div className="space-y-2">
            {[
              { label: "SEARCH DOMAINS", cmd: "SEARCH.EXE" },
              { label: "CREATE BACKORDER", cmd: "BACKORDER.EXE" },
              { label: "AI DOMAIN SUGGESTIONS", cmd: "AI_SUGGEST.EXE" },
            ].map((action) => (
              <button
                key={action.cmd}
                className="w-full flex items-center justify-between px-4 py-3 border border-green-700 hover:border-green-500 hover:bg-green-950/30 transition-all text-left"
              >
                <span className="text-green-500">{action.label}</span>
                <span className="text-green-600 text-xs">[{action.cmd}]</span>
              </button>
            ))}
          </div>

          {/* ASCII Art */}
          <pre className="text-green-700 text-xs mt-6 text-center">
{`
    ____________________
   /                   /|
  /                   / |
 /___________________/  |
 |  ZERO POINT      |   |
 |   DOMAINS        |   /
 |   V1.0.0         |  /
 |__________________|/
`}
          </pre>
        </div>
      </div>

      {/* Bottom Status */}
      <div className="border-t-2 border-green-500 pt-4 text-xs text-green-600 flex justify-between">
        <span>STATUS: {loading ? "LOADING..." : "READY"}</span>
        <span className="animate-pulse">&gt;_</span>
      </div>
    </div>
  );
};

export default Dashboard;
